/**
 * Edit Profile Page - Trade Loop v2.0
 *
 * Profile setup/edit page for users to complete their profile information
 * This page is shown after email verification during onboarding
 * and can be accessed later from settings to edit profile
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { useSubscription } from "@/hooks/useSubscription";
import { useTradeSections, Trade, TradeSection } from "@/hooks/useTrades";
import { useMarketTypes, MarketType } from "@/hooks/useMarketTypes";
import { useKeyboardInset } from "@/hooks/useKeyboardInset";
import { TradeCategory } from "@/components/profile/TradeCategorySelect";
import { api } from "@/lib/api";
import * as authService from "@/services/authService";
import { compressImage, isCompressibleImage } from "@/lib/imageUtils";
import { captureImageInto, isNativeCaptureAvailable } from "@/services/mediaCapture";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/phone-input";
import { PhoneVerificationModal } from "@/components/ui/PhoneVerificationModal";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ProfessionalProfileGate } from "@/components/subscription";
import { TradeCategorySelect } from "@/components/profile/TradeCategorySelect";
import { AdditionalTradesSelect } from "@/components/profile/AdditionalTradesSelect";
import {
  Upload,
  X,
  Building2,
  User,
  MapPin,
  Briefcase,
  Phone,
  Mail,
  Save,
  Loader2,
  ImageIcon,
  Check,
  AlertCircle,
} from "lucide-react";
import StateGatedServiceAreaPicker from "@/components/ui/StateGatedServiceAreaPicker";
import { GeoResult } from "@/lib/location-data";

interface ServiceArea {
  city: string; // For county entries this stores "County Name County".
  state: string;
}

export default function EditProfilePage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { hasFeature, isLoading: subscriptionLoading } = useSubscription();
  const { isKeyboardVisible, keyboardOverlap } = useKeyboardInset();

  // Fetch trades grouped by section from database (with IDs)
  const { data: tradeSections = [], isLoading: tradesLoading } = useTradeSections();

  // Fetch market types from database (with IDs)
  const { data: marketTypes = [], isLoading: marketTypesLoading } = useMarketTypes();

  // Feature flags for tier-based gating
  // Note: These default to Starter (most restrictive) until subscription data loads
  const hasProfessionalProfile = hasFeature('professionalProfile');
  const hasUnlimitedTrades = hasFeature('unlimitedTrades');
  const maxTrades = hasUnlimitedTrades ? Infinity : 0;

  // Form state
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Phone verification
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  // Contact info
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // Company info
  const [company, setCompany] = useState("");
  const [companyBio, setCompanyBio] = useState("");
  const [companyLogoUrl, setCompanyLogoUrl] = useState("");
  const [companyLogoFile, setCompanyLogoFile] = useState<File | null>(null);
  const [companyLogoPreview, setCompanyLogoPreview] = useState("");

  // Personal info
  const [personalBio, setPersonalBio] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState("");

  // Trades & Markets - store full objects with IDs
  const [selectedTrades, setSelectedTrades] = useState<Trade[]>([]);
  const [selectedMarketTypes, setSelectedMarketTypes] = useState<MarketType[]>([]);

  // Primary Trade - store full object like trades
  const [selectedTradeCategory, setSelectedTradeCategory] = useState<TradeCategory | null>(null);

  // Service Areas
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);

  // Privacy toggles (moved here from AccountPage so users control visibility next to the
  // field it applies to). Reads/writes go through /api/auth/profile — the caller's own row —
  // NOT /api/user-profiles/:id, which is service-role and answers 403 to a regular user.
  const [showEmailToConnections, setShowEmailToConnections] = useState(false);
  const [showPhoneToConnections, setShowPhoneToConnections] = useState(false);
  const [isUpdatingPrivacy, setIsUpdatingPrivacy] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const { profile } = await api.get<{ profile: Record<string, any> }>('/api/auth/profile');
        setShowEmailToConnections(profile?.showEmailToConnections ?? false);
        setShowPhoneToConnections(profile?.showPhoneToConnections ?? false);
      } catch (error) {
        console.error('Failed to fetch privacy settings:', error);
      }
    })();
  }, [user?.id]);

  const updatePrivacy = async (
    field: 'showEmailToConnections' | 'showPhoneToConnections',
    value: boolean,
  ) => {
    setIsUpdatingPrivacy(true);
    try {
      await authService.updateProfile({ [field]: value });
      showSuccess(
        'Privacy Settings Updated',
        `Your ${field === 'showEmailToConnections' ? 'email' : 'phone'} visibility has been updated`,
      );
    } catch (error) {
      console.error('Failed to update privacy settings:', error);
      showError('Update Failed', 'Could not update privacy settings. Please try again.');
      if (field === 'showEmailToConnections') {
        setShowEmailToConnections(!value);
      } else {
        setShowPhoneToConnections(!value);
      }
    } finally {
      setIsUpdatingPrivacy(false);
    }
  };

  // File input refs
  const profileImageInputRef = useRef<HTMLInputElement>(null);
  const companyLogoInputRef = useRef<HTMLInputElement>(null);

  // What the form was last loaded FOR. See the hydration effect below — this is what stops a
  // refreshed `user` object from wiping edits the user has not saved yet.
  const hydratedForRef = useRef<string | null>(null);

  // Load existing user data
  // Note: Feature-gated fields are NOT loaded for downgraded users
  // IMPORTANT: Wait for subscription data to load before populating feature-gated fields
  // This ensures downgraded users don't see their old Pro data during the brief loading period
  useEffect(() => {
    // Don't populate form until both user AND subscription data are loaded
    // This prevents race condition where form shows Pro features before we know the actual plan
    if (!user || subscriptionLoading) {
      return;
    }

    // LOAD THE FORM ONCE PER USER, NOT ON EVERY `user` OBJECT.
    //
    // This effect overwrites every field from the server, so it must not re-run while the user
    // is editing — it would silently discard whatever they had changed. `refreshUser()` returns
    // a NEW object each time even when the data is identical, so any refresh re-ran the whole
    // body and reset the form.
    //
    // That is not hypothetical, and on native it is reliable rather than rare: the native camera
    // backgrounds the app, so returning from it fires Capacitor's `resume`, which
    // `useEntitlementRefreshOnResume` answers with `refreshUser()`. The reported symptom was a
    // picked profile photo appearing for a moment and then snapping back to the saved one —
    // preview set, resume lands, form re-hydrated. Every other field had the same problem; the
    // photo is just the one you can see.
    //
    // The key deliberately includes the entitlement flags rather than only the id. Re-gating on
    // an entitlement change is the point of the branches below — company fields unlock on
    // upgrade and clear on downgrade — and Card 18's whole resume-refresh exists so a user who
    // subscribes in a browser sees it on return. So a plan change DOES re-hydrate (and may drop
    // in-progress edits, which is the right trade for a rare, deliberate state change); a
    // routine refresh of the same user on the same plan does not.
    const hydrationKey = `${user.id}|${hasProfessionalProfile}|${hasUnlimitedTrades}`;
    if (hydratedForRef.current === hydrationKey) {
      return;
    }
    hydratedForRef.current = hydrationKey;

    setPhone(user.phone || "");
    setEmail(user.email || "");
    setFirstName(user.firstName || "");
    setLastName(user.lastName || "");

    // Company info is only loaded for Pro/Grow users (professional profile feature)
    if (hasProfessionalProfile) {
      setCompany(user.company || "");
      setCompanyBio(user.companyBio || "");
      setCompanyLogoUrl(user.companyLogoUrl || "");
      setCompanyLogoPreview(user.companyLogoUrl || "");
    } else {
      // Clear company fields for Starter users (downgrade protection)
      setCompany("");
      setCompanyBio("");
      setCompanyLogoUrl("");
      setCompanyLogoPreview("");
    }

    setPersonalBio(user.personalBio || "");
    setProfileImageUrl(user.profileImageUrl || "");
    setProfileImagePreview(user.profileImageUrl || "");

    // Fetch user's trades with full objects from the workflow endpoint
    const fetchUserTrades = async () => {
      console.log('[EditProfilePage] ===== FETCH USER TRADES CALLED =====');
      console.log('[EditProfilePage] User ID:', user.id);
      try {
        console.log('[EditProfilePage] Fetching trades for user:', user.id);
        const response = await api.get(`/api/workflows/user-profiles/${user.id}/trades`);
        console.log('[EditProfilePage] Trades response:', response);
        console.log('[EditProfilePage] Trades response.data:', response.data);

        // Handle both wrapped ({ success: true, data: [...] }) and unwrapped ([...]) responses
        const trades = Array.isArray(response.data) ? response.data : response.data?.data;
        console.log('[EditProfilePage] Trades array:', trades);

        if (trades && Array.isArray(trades) && trades.length > 0) {
          const tradeObjects: Trade[] = trades.map((trade: any) => ({
            id: trade.id,
            name: trade.name,
            slug: trade.slug,
            section: trade.section || null,
            displayOrder: trade.displayOrder || null,
            isSectionHeader: trade.isSectionHeader || false
          }));

          console.log('[EditProfilePage] Mapped trade objects:', tradeObjects);
          console.log('[EditProfilePage] hasUnlimitedTrades:', hasUnlimitedTrades, 'maxTrades:', maxTrades);

          // Apply subscription tier limits
          if (hasUnlimitedTrades) {
            setSelectedTrades(tradeObjects);
            console.log('[EditProfilePage] Set selected trades (unlimited):', tradeObjects);
          } else {
            const limited = tradeObjects.slice(0, maxTrades);
            setSelectedTrades(limited);
            console.log('[EditProfilePage] Set selected trades (limited):', limited);
          }
        } else {
          console.log('[EditProfilePage] No trade data in response');
        }
      } catch (error) {
        console.error('[EditProfilePage] Failed to fetch trades:', error);
        // Fallback to empty array on error
        setSelectedTrades([]);
      }
    };

    fetchUserTrades();

    // Fetch user's market types with full objects from the workflow endpoint
    const fetchUserMarketTypes = async () => {
      console.log('[EditProfilePage] ===== FETCH USER MARKET TYPES CALLED =====');
      console.log('[EditProfilePage] User ID:', user.id);
      try {
        console.log('[EditProfilePage] Fetching market types for user:', user.id);
        const response = await api.get(`/api/workflows/user-profiles/${user.id}/market-types`);
        console.log('[EditProfilePage] Market types response:', response);
        console.log('[EditProfilePage] Market types response.data:', response.data);

        // Handle both wrapped ({ success: true, data: [...] }) and unwrapped ([...]) responses
        const marketTypes = Array.isArray(response.data) ? response.data : response.data?.data;
        console.log('[EditProfilePage] Market types array:', marketTypes);

        if (marketTypes && Array.isArray(marketTypes) && marketTypes.length > 0) {
          const marketTypeObjects: MarketType[] = marketTypes.map((mt: any) => ({
            id: mt.id,
            name: mt.name,
            slug: mt.slug
          }));
          console.log('[EditProfilePage] Mapped market type objects:', marketTypeObjects);
          setSelectedMarketTypes(marketTypeObjects);
          console.log('[EditProfilePage] Set selected market types:', marketTypeObjects);
        } else {
          console.log('[EditProfilePage] No market type data in response');
        }
      } catch (error) {
        console.error('[EditProfilePage] Failed to fetch market types:', error);
        setSelectedMarketTypes([]);
      }
    };

    fetchUserMarketTypes();

    // Fetch user's primary trade category with full object from the profile workflow endpoint
    const fetchUserTradeCategory = async () => {
      console.log('[EditProfilePage] ===== FETCH USER TRADE CATEGORY CALLED =====');
      try {
        const response = await api.get(`/api/workflows/profile/me/primary-trade`);
        console.log('[EditProfilePage] Trade category response:', response);
        console.log('[EditProfilePage] Trade category response.data:', response.data);

        // Handle both wrapped ({ success: true, data: { primaryTradeCategory: ... } }) and unwrapped responses
        let tradeData = null;
        if (response.data?.data?.primaryTradeCategory) {
          tradeData = response.data.data.primaryTradeCategory;
        } else if (response.data?.primaryTradeCategory) {
          tradeData = response.data.primaryTradeCategory;
        }

        console.log('[EditProfilePage] Extracted trade data:', tradeData);

        if (tradeData) {
          const tradeCategoryObject: TradeCategory = {
            id: tradeData.id,
            name: tradeData.name,
            description: tradeData.description || null,
            icon_name: tradeData.icon_name || null
          };
          console.log('[EditProfilePage] Setting trade category:', tradeCategoryObject);
          setSelectedTradeCategory(tradeCategoryObject);
        } else {
          console.log('[EditProfilePage] No trade category data, setting null');
          setSelectedTradeCategory(null);
        }
      } catch (error) {
        console.error('[EditProfilePage] Failed to fetch trade category:', error);
        setSelectedTradeCategory(null);
      }
    };

    fetchUserTradeCategory();

    // Parse service areas
    if (user.serviceAreas && user.serviceAreas.length > 0) {
      const parsedAreas = user.serviceAreas.map((area: string) => {
        const parts = area.split(", ");
        return {
          city: parts[0] || area,
          state: parts[1] || "",
        };
      });
      setServiceAreas(parsedAreas);
    }
  }, [user, subscriptionLoading, hasProfessionalProfile, hasUnlimitedTrades, maxTrades]);

  // Handle profile image selection with auto-compression
  const handleProfileImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await processProfileImage(file);
  };

  // Shared by the web file input and the native camera — both hand over a plain File.
  const processProfileImage = async (file: File) => {
    let processedFile = file;
    if (file.size > 5 * 1024 * 1024) {
      if (isCompressibleImage(file)) {
        try {
          processedFile = await compressImage(file, 5, 0.85);
        } catch {
          showError("Compression failed", "Could not compress image. Please use a smaller file.");
          return;
        }
      } else {
        showError("File too large", "Profile image must be less than 5MB");
        return;
      }
    }
    setProfileImageFile(processedFile);
    setProfileImagePreview(URL.createObjectURL(processedFile));
  };

  // Handle company logo selection with auto-compression
  const handleCompanyLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await processCompanyLogo(file);
  };

  // Shared by the web file input and the native camera — both hand over a plain File.
  const processCompanyLogo = async (file: File) => {
    let processedFile = file;
    if (file.size > 5 * 1024 * 1024) {
      if (isCompressibleImage(file)) {
        try {
          processedFile = await compressImage(file, 5, 0.85);
        } catch {
          showError("Compression failed", "Could not compress image. Please use a smaller file.");
          return;
        }
      } else {
        showError("File too large", "Company logo must be less than 5MB");
        return;
      }
    }
    setCompanyLogoFile(processedFile);
    setCompanyLogoPreview(URL.createObjectURL(processedFile));
  };

  // On native both pickers are the app's own camera / photo-library sheet
  // (@capacitor/camera); on web they stay the hidden file inputs below.
  const openProfileImagePicker = () => {
    if (isNativeCaptureAvailable()) {
      void captureImageInto(processProfileImage, showError);
      return;
    }
    profileImageInputRef.current?.click();
  };

  const openCompanyLogoPicker = () => {
    if (isNativeCaptureAvailable()) {
      void captureImageInto(processCompanyLogo, showError);
      return;
    }
    companyLogoInputRef.current?.click();
  };

  // Upload image via backend API
  const uploadImage = async (
    file: File,
    bucket: string,
    path: string
  ): Promise<string | null> => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      // Determine endpoint based on bucket/path
      let endpoint = "/api/files/upload";
      if (bucket === "avatars" || path === "profiles") {
        endpoint = "/api/files/avatar";
      } else if (bucket === "company-logos") {
        endpoint = "/api/files/company-logo";
      }

      const response = await api.post(endpoint, formData);

      if (response?.data?.url) {
        return response.data.url;
      }

      return null;
    } catch (error) {
      console.error("Upload error:", error);
      return null;
    }
  };

  // Toggle market type selection
  const toggleMarketType = (marketType: MarketType) => {
    setSelectedMarketTypes((prev) => {
      const isSelected = prev.some(m => m.id === marketType.id);
      return isSelected
        ? prev.filter((m) => m.id !== marketType.id)
        : [...prev, marketType];
    });
  };

  // Check if service area already exists (case-insensitive)
  const isDuplicateServiceArea = (newArea: ServiceArea) => {
    return serviceAreas.some(
      (area) =>
        area.city.toLowerCase() === newArea.city.toLowerCase() &&
        area.state.toLowerCase() === newArea.state.toLowerCase()
    );
  };

  // Remove service area
  const removeServiceArea = (index: number) => {
    setServiceAreas((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle sending verification code
  const handleSendVerificationCode = async () => {
    try {
      await api.post('/api/auth/phone/send-code', { phoneNumber: phone });
      showSuccess('Verification code sent!', 'Check your phone for the SMS code');
    } catch (error: any) {
      showError('Failed to send code', error.message || 'Please try again later');
      throw error;
    }
  };

  // Handle verifying phone with OTP code
  const handleVerifyPhone = async (code: string) => {
    try {
      await api.post('/api/auth/phone/verify-code', { phoneNumber: phone, code });
      await refreshUser(); // Refresh user data to get updated phoneConfirmedAt
      showSuccess('Phone verified!', 'Your phone number has been verified');
    } catch (error: any) {
      showError('Verification failed', error.message || 'Invalid code');
      throw error;
    }
  };

  // Handle form submission
  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setIsSaving(true);

    try {
      let newProfileImageUrl = profileImageUrl;
      let newCompanyLogoUrl = companyLogoUrl;

      // Upload profile image if changed
      if (profileImageFile) {
        const uploadedUrl = await uploadImage(
          profileImageFile,
          "avatars",
          "profiles"
        );
        if (uploadedUrl) {
          newProfileImageUrl = uploadedUrl;
        } else {
          showError("Upload failed", "Failed to upload profile image");
        }
      }

      // Upload company logo if changed (only for Pro/Grow users)
      if (companyLogoFile && hasProfessionalProfile) {
        const uploadedUrl = await uploadImage(
          companyLogoFile,
          "avatars",
          "company-logos"
        );
        if (uploadedUrl) {
          newCompanyLogoUrl = uploadedUrl;
        } else {
          showError("Upload failed", "Failed to upload company logo");
        }
      }

      // Format service areas for storage
      const formattedServiceAreas = serviceAreas.map((area) =>
        area.state ? `${area.city}, ${area.state}` : area.city
      );

      // Prepare the payload
      // Phone is included here — the backend saves it to auth.users, and the
      // database trigger automatically syncs it to user_profiles.trig_user_phone
      // Feature-gated fields are only included for users with access (downgrade protection)
      // NOTE: trades and marketTypes are NOT included here - they're updated via separate workflow endpoints
      const payload: Record<string, any> = {
        firstName,
        lastName,
        phone,
        personalBio,
        profileImageUrl: newProfileImageUrl,
        serviceAreas: formattedServiceAreas,  // Send as array, not JSON string
        primaryTradeId: selectedTradeCategory?.id || null,  // Extract ID from trade category object
        onboardingComplete: true,
      };

      // Only include company info for Pro/Grow users (professional profile feature)
      if (hasProfessionalProfile) {
        payload.company = company;
        payload.companyBio = companyBio;
        payload.companyLogoUrl = newCompanyLogoUrl;
      }

      console.log('[EditProfilePage] Submitting profile update with payload:', payload);

      // Update profile via API
      await api.put("/api/auth/profile", payload);

      // Update trades via junction table workflow endpoint
      // Extract IDs and apply subscription tier limits
      const tradeIds = (hasUnlimitedTrades
        ? selectedTrades
        : selectedTrades.slice(0, maxTrades)
      ).map(trade => trade.id);

      await api.put(`/api/workflows/user-profiles/${user.id}/trades`, {
        trades: tradeIds
      });

      // Update market types via junction table workflow endpoint
      // Extract IDs from MarketType objects
      const marketTypeIds = selectedMarketTypes.map(mt => mt.id);

      await api.put(`/api/workflows/user-profiles/${user.id}/market-types`, {
        marketTypes: marketTypeIds
      });

      // Refresh user data
      await refreshUser();

      showSuccess("Profile updated", "Your profile has been saved successfully");

      // Navigate to dashboard or settings based on context
      if (!user?.onboardingComplete) {
        navigate("/dashboard");
      } else {
        navigate("/settings");
      }
    } catch (error: any) {
      console.error("[EditProfilePage] Save error:", error);
      console.error("[EditProfilePage] Error response:", error.response);
      console.error("[EditProfilePage] Error data:", error.response?.data);
      showError("Save failed", error.response?.data?.message || error.message || "Failed to save profile");
    } finally {
      setIsSaving(false);
    }
  };

  // User initials for avatar fallback
  const userInitials =
    user?.firstName && user?.lastName
      ? `${user.firstName[0]}${user.lastName[0]}`
      : user?.email?.[0]?.toUpperCase() || "U";

  return (
    <AppLayout
      activeTab="profile"
      hideNavigation={!user?.onboardingComplete}
      title={user?.onboardingComplete ? "Edit Profile" : "Complete Your Profile"}
      backLink={user?.onboardingComplete ? "/settings" : undefined}
    >
      <div className="max-w-3xl mx-auto px-4 pb-[120px] md:pb-[80px]">
        <form onSubmit={handleSubmit} className="space-y-6 pt-4">
          {/* Profile Picture & Company Logo */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-blue-600" />
                Photos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Profile Picture */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Profile Picture</Label>
                  <div className="flex items-center gap-4">
                    <Avatar className="w-20 h-20 ring-4 ring-gray-100">
                      <AvatarImage src={profileImagePreview} />
                      <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-xl font-bold">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <input
                        ref={profileImageInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleProfileImageChange}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={openProfileImagePicker}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Photo
                      </Button>
                      <p className="text-xs text-gray-500 mt-1">
                        JPG, PNG. Max 5MB.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Company Logo - Pro+ Feature */}
                <ProfessionalProfileGate
                  fallback={
                    <div className="space-y-3">
                      <Label className="text-sm font-semibold flex items-center gap-2">
                        Company Logo
                        <span className="text-xs font-normal text-orange-600 bg-orange-50 px-2 py-0.5 rounded">Pro+</span>
                      </Label>
                      <div className="flex items-center gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <Building2 className="w-12 h-12 text-gray-300" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-600">Company branding is available on Pro and Grow plans</p>
                        </div>
                      </div>
                    </div>
                  }
                >
                  <div className="space-y-3">
                    <Label className="text-sm font-semibold">Company Logo</Label>
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden ring-4 ring-gray-100">
                        {companyLogoPreview ? (
                          <img
                            src={companyLogoPreview}
                            alt="Company logo"
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <Building2 className="w-8 h-8 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <input
                          ref={companyLogoInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleCompanyLogoChange}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={openCompanyLogoPicker}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Logo
                        </Button>
                        <p className="text-xs text-gray-500 mt-1">
                          JPG, PNG. Max 5MB.
                        </p>
                      </div>
                    </div>
                  </div>
                </ProfessionalProfileGate>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-blue-600" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-sm font-semibold">
                    First Name <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="John"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-sm font-semibold">
                    Last Name <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Doe"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="email" className="text-sm font-semibold">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      disabled
                      className="pl-10 bg-gray-50"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Email cannot be changed
                  </p>
                  {/* On md+, the email column has an extra "Email cannot be changed" caption the
                      phone column does not, so `md:mt-auto` pins the toggle to the bottom of the
                      grid cell — keeping the two toggles aligned when they render side by side.
                      Grid cells stretch to match, so this works without a fixed height. The
                      column uses `flex flex-col gap-2` rather than `space-y-2` on purpose —
                      space-y-* has higher CSS specificity than mt-auto and would overrule it.
                      Mobile keeps the same look because gap-2 == space-y-2 visually. */}
                  <div className="flex items-center justify-between gap-3 pt-1 md:mt-auto">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Show Email to Connections
                      </p>
                      <p className="text-xs text-gray-500">
                        Allow connected users to see your email address
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={showEmailToConnections}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          setShowEmailToConnections(newValue);
                          updatePrivacy('showEmailToConnections', newValue);
                        }}
                        disabled={isUpdatingPrivacy}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                    </label>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="phone" className="text-sm font-semibold">
                    Phone Number
                  </Label>
                  <div className="space-y-2">
                    <PhoneInput
                      id="phone"
                      name="phone"
                      value={phone}
                      onChange={setPhone}
                    />

                    {/* Phone Verification Status - HIDDEN: Not supporting phone verification for now */}
                    {false && phone && (
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          {user?.phoneConfirmedAt ? (
                            <>
                              <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                                <Check className="w-3 h-3 text-green-600" />
                              </div>
                              <span className="text-sm text-green-700 font-medium">
                                Verified
                              </span>
                            </>
                          ) : (
                            <>
                              <div className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center">
                                <AlertCircle className="w-3 h-3 text-amber-600" />
                              </div>
                              <span className="text-sm text-amber-700 font-medium">
                                Not Verified
                              </span>
                            </>
                          )}
                        </div>

                        {!user?.phoneConfirmedAt && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setShowVerificationModal(true)}
                            className="text-xs"
                          >
                            Verify Phone
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Sibling of the phone-input wrapper (not nested inside it) so `md:mt-auto`
                      pushes to the bottom of the OUTER column and aligns with the email column's
                      toggle. See the matching comment on the email side. */}
                  <div className="flex items-center justify-between gap-3 pt-1 md:mt-auto">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Show Phone to Connections
                      </p>
                      <p className="text-xs text-gray-500">
                        Allow connected users to see your phone number
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={showPhoneToConnections}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          setShowPhoneToConnections(newValue);
                          updatePrivacy('showPhoneToConnections', newValue);
                        }}
                        disabled={isUpdatingPrivacy}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                    </label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Primary Trade Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-blue-600" />
                Primary Trade
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TradeCategorySelect
                value={selectedTradeCategory}
                onChange={setSelectedTradeCategory}
                disabled={isSaving}
                showLabel={true}
              />
            </CardContent>
          </Card>

          {/* Additional Trades - directly below Primary Trade */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-blue-600" />
                Additional Trades
              </CardTitle>
            </CardHeader>
            <CardContent>
              {hasUnlimitedTrades ? (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    Select all additional trades that apply to your business
                  </p>
                  <AdditionalTradesSelect
                    value={selectedTrades}
                    onChange={setSelectedTrades}
                    disabled={isSaving}
                    maxTrades={maxTrades}
                    showLabel={false}
                    onLimitReached={() => {
                      showError('Trade limit reached', 'Upgrade to Pro for unlimited trades.');
                    }}
                  />
                </>
              ) : (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm text-gray-700 font-medium mb-1">
                    Upgrade to add additional trades
                  </p>
                  <p className="text-xs text-gray-500">
                    Pro and Grow plans allow unlimited additional trades for your profile.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Company Information - Pro+ Feature */}
          <ProfessionalProfileGate
            fallback={
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    Company Information
                    <span className="text-xs font-normal text-orange-600 bg-orange-50 px-2 py-0.5 rounded">Pro+</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-600">
                      Company name and bio are available on Pro and Grow plans. Upgrade to showcase your business!
                    </p>
                  </div>
                </CardContent>
              </Card>
            }
          >
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Company Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-sm font-semibold">
                    Company Name
                  </Label>
                  <Input
                    id="company"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Your company name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="companyBio" className="text-sm font-semibold">
                    Company Bio
                  </Label>
                  <Textarea
                    id="companyBio"
                    value={companyBio}
                    onChange={(e) => setCompanyBio(e.target.value)}
                    placeholder="Tell us about your company..."
                    rows={4}
                  />
                  <p className="text-xs text-gray-500">
                    Describe your company, services, and what sets you apart
                  </p>
                </div>
              </CardContent>
            </Card>
          </ProfessionalProfileGate>

          {/* Personal Bio */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                About You
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="personalBio" className="text-sm font-semibold">
                  Personal Bio
                </Label>
                <Textarea
                  id="personalBio"
                  value={personalBio}
                  onChange={(e) => setPersonalBio(e.target.value)}
                  placeholder="Tell us about yourself and your experience..."
                  rows={4}
                />
                <p className="text-xs text-gray-500">
                  Share your background, expertise, and professional interests
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Market Types */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Market Types Served
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Select the types of markets you serve
              </p>
              <div className="flex flex-wrap gap-3">
                {marketTypesLoading ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Loading market types...</span>
                  </div>
                ) : marketTypes.map((marketType) => {
                  const isSelected = selectedMarketTypes.some(m => m.id === marketType.id);
                  return (
                    <label
                      key={marketType.id}
                      className={`flex items-center gap-2 px-4 py-3 rounded-xl cursor-pointer transition-all ${
                        isSelected
                          ? "bg-green-50 border-2 border-green-500 shadow-sm"
                          : "bg-gray-50 border-2 border-transparent hover:border-gray-200"
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleMarketType(marketType)}
                      />
                      <span className="font-medium">{marketType.name}</span>
                    </label>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Service Areas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-600" />
                Service Areas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">
                Pick the state first, then search for the city or county to
                add. Picking a county automatically matches partners who
                selected any city inside that county.
              </p>
              <StateGatedServiceAreaPicker
                onSelect={(result: GeoResult) => {
                  const newArea: ServiceArea =
                    result.type === 'county'
                      ? { city: `${result.name} County`, state: result.stateCode }
                      : { city: result.name, state: result.stateCode };
                  if (!isDuplicateServiceArea(newArea)) {
                    setServiceAreas((prev) => [...prev, newArea]);
                  }
                }}
                mapCenterHint={
                  serviceAreas[0]
                    ? `${serviceAreas[0].city}, ${serviceAreas[0].state}`
                    : undefined
                }
                disabled={isSaving}
              />

              {serviceAreas.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {serviceAreas.map((area, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-100 text-indigo-800 text-sm font-medium rounded-full"
                    >
                      <MapPin className="w-3 h-3" />
                      {area.state ? `${area.city}, ${area.state}` : area.city}
                      <button
                        type="button"
                        onClick={() => removeServiceArea(index)}
                        className="hover:bg-indigo-200 rounded-full p-0.5 ml-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

        </form>
      </div>

      {/* Sticky Footer — sits above the bottom nav, and flush on the keyboard
          while it is open. The nav unmounts on keyboard open
          (BottomNavigation.tsx), so holding its height back would leave the bar
          floating in the gap between the two. `keyboardOverlap` is 0 wherever
          the container is already resized (native Capacitor, Android Chrome)
          and the keyboard's height where it is not (iOS Safari) —
          see useKeyboardInset. */}
      <div
        className="fixed left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.08)]"
        style={{
          bottom: isKeyboardVisible
            ? `${keyboardOverlap}px`
            : 'calc(65px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div className="max-w-3xl mx-auto px-4 py-3 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-8"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Profile
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Phone Verification Modal */}
      <PhoneVerificationModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        phoneNumber={phone}
        onVerify={handleVerifyPhone}
        onResendCode={handleSendVerificationCode}
      />
    </AppLayout>
  );
}
